import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import OmniInbox from "@/components/OmniInbox";
import ConversationView from "@/components/ConversationView";
import AiCopilot from "@/components/AiCopilot";
import CustomerProfilePanel from "@/components/CustomerProfilePanel";
import PreviewDialPanel from "@/components/PreviewDialPanel";
import LiveCallControlBar from "@/components/LiveCallControlBar";
import { useCallTimer } from "@/hooks/useCallTimer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Headset, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSoftphone } from "@/contexts/SoftphoneContext";

export default function Workspace() {
  const ctx = useOutletContext() || {};
  const { aiCopilot, presence } = ctx;
  const isOffline = (presence || "Offline") === "Offline";
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [contact, setContact] = useState(null);
  const [inboxView, setInboxView] = useState("list");
  const [callDriven, setCallDriven] = useState(false);  // active-view was opened by a call → auto-revert on hangup
  const [callActive, setCallActive] = useState(false);  // a voice call is bridged → AI copilot must follow the CALL
  const [customerGone, setCustomerGone] = useState(false);  // customer disconnected → dead call (agent still in wrap)
  const [omniEnabled, setOmniEnabled] = useState(true);  // tenant Omnichannel section toggle
  const sp = useSoftphone();
  const lastPoppedRef = useRef(null);

  // Resolve whether the tenant has the Omnichannel workspace section enabled.
  useEffect(() => {
    api.get("/me/channels")
      .then((r) => setOmniEnabled(r.data?.omnichannel_enabled !== false))
      .catch(() => setOmniEnabled(true));
  }, []);

  // Signal that the workspace (and its screen-pop listeners) is mounted, so
  // startManualDial()/popScreenOnWorkspace() fire the dial/pop only once we're
  // ready. Deferred a tick so the dialer:bridged listener (registered in a
  // later useEffect) is attached BEFORE the ready signal triggers a pop.
  useEffect(() => {
    const t = setTimeout(() => window.dispatchEvent(new CustomEvent("c7:workspace-ready")), 0);
    return () => { clearTimeout(t); window.dispatchEvent(new CustomEvent("c7:workspace-unmount")); };
  }, []);

  useEffect(() => {
    if (!selectedSummary) return;
    api.get(`/conversations/${selectedSummary.id}`).then((r) => {
      setConversation(r.data.conversation);
      setContact(r.data.contact);
      if (r.data.conversation?.live_call) setInboxView("active");
    });
    api.post(`/conversations/${selectedSummary.id}/read`).catch(() => {});
  }, [selectedSummary?.id]);

  // Omnichannel screen-recording trigger — signal when the agent is actively
  // handling a non-voice conversation so the recorder can capture the "omni" mode.
  useEffect(() => {
    const ch = conversation?.primary_channel;
    const active = !!conversation?.id && !!ch && ch !== "voice";
    window.dispatchEvent(new CustomEvent("c7:conversation-active", {
      detail: { active, conversation_id: conversation?.id, channel: ch },
    }));
    return () => {
      window.dispatchEvent(new CustomEvent("c7:conversation-active", { detail: { active: false } }));
    };
  }, [conversation?.id, conversation?.primary_channel]);

  // Near-real-time refresh of the OPEN conversation for channels whose messages
  // are stored directly on the conversation doc (WATI WhatsApp, SMS/Email
  // simulator, voicemail). PostProxy threads self-refresh inside ConversationView.
  // Re-fetches on any matching `channel:inbound` WS event + an 8s poll fallback.
  useEffect(() => {
    const conv = conversation;
    const ch = conv?.primary_channel;
    if (!conv?.id || !ch || ch === "voice" || conv.source === "postproxy") return;
    let stopped = false;
    const refresh = async () => {
      try {
        const r = await api.get(`/conversations/${conv.id}`);
        if (stopped || !r.data?.conversation) return;
        setConversation((cur) => {
          if (!cur || cur.id !== r.data.conversation.id) return cur;
          const next = r.data.conversation;
          // Skip if nothing changed (avoid clobbering optimistic sends).
          const curLen = (cur.messages || []).length;
          const nextLen = (next.messages || []).length;
          if (nextLen < curLen) return cur;
          return next;
        });
        api.post(`/conversations/${conv.id}/read`).catch(() => {});
      } catch { /* transient */ }
    };
    const onInbound = (e) => { if (e?.detail?.conversation_id === conv.id) refresh(); };
    window.addEventListener("channel:inbound", onInbound);
    const iv = setInterval(refresh, 8000);
    return () => { stopped = true; clearInterval(iv); window.removeEventListener("channel:inbound", onInbound); };
  }, [conversation?.id, conversation?.primary_channel, conversation?.source]);

  // Slice 15 — report digital-channel activity to the Live Dashboard while a
  // (non-voice) conversation is open + the tab is visible. Re-pings every 10s;
  // clears on close / switch / unmount so supervisors see accurate "on
  // WhatsApp / Instagram / …" status with a live duration.
  useEffect(() => {
    const conv = conversation;
    const ch = conv?.primary_channel;
    const isDigital = !!(conv && ch && ch !== "voice");
    if (!isDigital) {
      api.post("/me/activity", { clear: true }).catch(() => {});
      return;
    }
    const ping = () => {
      if (document.visibilityState !== "visible") return;
      api.post("/me/activity", {
        conv_id: conv.id,
        channel: ch,
        peer_name: conv.contact_name || contact?.full_name || "",
      }).catch(() => {});
    };
    ping();
    const t = setInterval(ping, 10000);
    return () => {
      clearInterval(t);
      api.post("/me/activity", { clear: true }).catch(() => {});
    };
  }, [conversation?.id, conversation?.primary_channel, contact?.full_name]);

  // Screen-pop: when any softphone session is active, resolve contact by phone
  // and switch the inbox panel into "active" mode showing that contact's CRM card.
  useEffect(() => {
    // Find the most relevant session: prefer a ringing inbound, then talking, else first.
    const ringing = sp.sessions.find((s) => s.state === "ringing" && s.direction === "inbound");
    const talking = sp.sessions.find((s) => s.state === "talking");
    const focus = ringing || talking || sp.sessions[0] || null;

    if (focus) {
      if (lastPoppedRef.current === focus.id) return;  // already popped this session
      lastPoppedRef.current = focus.id;
      const phone = focus.peer;
      if (!phone) return;
      api.get("/contacts/by-phone", { params: { phone } })
        .then((r) => {
          setContact(r.data);
          setInboxView("active");
          setCallDriven(true);
          setCallActive(true);
          setCustomerGone(false);
          // We don't have a conversation; clear any stale one so the middle pane
          // doesn't show the previous customer's history.
          setSelectedSummary(null);
          setConversation(null);
        })
        .catch(() => {
          // Unknown caller — show a synthetic contact card with just name + phone
          setContact({
            id: `unknown-${focus.id}`,
            full_name: focus.peer_name || "Unknown caller",
            phone: focus.peer,
            company: "",
            email: "",
            unknown: true,
            disposition: "",
            ltv: 0, tickets_open: 0, plan: "—",
          });
          setInboxView("active");
          setCallDriven(true);
          setCallActive(true);
          setCustomerGone(false);
          setSelectedSummary(null);
          setConversation(null);
        });
    } else if (callDriven) {
      // All sessions ended — return to inbox list (only if we were call-driven)
      setInboxView("list");
      setCallDriven(false);
      setCallActive(false);
      setCustomerGone(false);
      lastPoppedRef.current = null;
    }
  }, [sp.sessions]);

  // Listen for "Save as contact" from the unknown-caller card → swap in the new contact
  useEffect(() => {
    function onContactCreated(e) {
      const c = e.detail;
      if (c?.id) setContact(c);
    }
    window.addEventListener("contact:created", onContactCreated);
    return () => window.removeEventListener("contact:created", onContactCreated);
  }, []);

  // Listen for "open from call history" → screen-pop by phone, same path
  useEffect(() => {
    function onPop(e) {
      const phone = e.detail?.phone;
      const name = e.detail?.name;
      if (!phone) return;
      api.get("/contacts/by-phone", { params: { phone } })
        .then((r) => {
          setContact(r.data);
          setInboxView("active");
          setCallDriven(false);
          setSelectedSummary(null);
          setConversation(null);
        })
        .catch(() => {
          setContact({ id: `hist-${Date.now()}`, full_name: name || "Unknown caller", phone, unknown: true, ltv: 0, tickets_open: 0, plan: "—" });
          setInboxView("active");
          setCallDriven(false);
          setSelectedSummary(null);
          setConversation(null);
        });
    }
    // Slice 3.5b — Predictive-dialer screen-pop. When the backend's ARI
    // Stasis daemon bridges a customer leg into THIS agent's ConfBridge,
    // useSoftphone dispatches "dialer:bridged" with the lead id. We resolve
    // the lead → CRM contact and pop the same panel used for inbound calls.
    async function onDialerBridged(e) {
      const leadId = e.detail?.lead_id;
      const phone = e.detail?.phone;
      const fallbackName = e.detail?.name || "";
      const source = e.detail?.source || "predictive";
      try {
        let popped = null;
        if (leadId) {
          try {
            const r = await api.get(`/leads/${leadId}/screen-pop`);
            popped = r.data?.contact || r.data;
          } catch { /* fall back to by-phone */ }
        }
        if (!popped && phone) {
          try {
            const r = await api.get("/contacts/by-phone", { params: { phone } });
            popped = r.data;
          } catch { /* unknown */ }
        }
        // If still no contact AND the call was a manual outbound dial,
        // AUTO-CREATE a contact in the CRM so the agent can take notes,
        // tag, and link the call. (Inbound calls already create a contact
        // via the existing call-pop pipeline; predictive uses the lead row.)
        if (!popped && phone && source === "manual_dial") {
          try {
            const r = await api.post("/contacts", {
              full_name: fallbackName || phone,
              phone:     phone,
              source:    "manual_dial",
            });
            popped = r.data;
            try { toast.success(`New contact created: ${phone}`); } catch {}
          } catch { /* fall through to unknown stub */ }
        }
        setContact(popped || {
          id: `dial-${Date.now()}`,
          full_name: fallbackName || "Outbound call",
          phone: phone || "",
          unknown: true,
          ltv: 0, tickets_open: 0, plan: "—",
        });
        setInboxView("active");
        setCallDriven(true);
        setCallActive(true);
        setCustomerGone(false);
        setSelectedSummary(null);
        setConversation(null);
      } catch { /* swallow — popup is best-effort */ }
    }
    // ─── Live-call ended → clear the CRM screen-pop ────────────────
    // Fired by useSipEngine (live WebRTC) AND useSoftphone (simulator
    // backend) when a call session terminates. Both paths bypass the
    // `sp.sessions` array (live mode never populates it; simulator
    // populates it but the cleanup race below clears it). This handler
    // is the single source of truth for "the call is over → put the
    // workspace back to inbox view".
    function onDialerEnded() {
      setContact(null);
      setConversation(null);
      setSelectedSummary(null);
      setInboxView("list");
      setCallDriven(false);
      setCallActive(false);
      setCustomerGone(false);
      lastPoppedRef.current = null;
    }
    // Customer disconnected first (predictive/inbound dialer path) → the agent
    // is now in a DEAD call while they wrap up. Flip the badge green → red.
    function onCustomerHangup() {
      setCustomerGone(true);
    }
    window.addEventListener("callhistory:pop", onPop);
    window.addEventListener("dialer:bridged", onDialerBridged);
    window.addEventListener("dialer:ended", onDialerEnded);
    window.addEventListener("dialer:customer-hangup", onCustomerHangup);
    return () => {
      window.removeEventListener("callhistory:pop", onPop);
      window.removeEventListener("dialer:bridged", onDialerBridged);
      window.removeEventListener("dialer:ended", onDialerEnded);
      window.removeEventListener("dialer:customer-hangup", onCustomerHangup);
    };
  }, []);

  // Call status for the live-call badge. We DEFAULT to "live" whenever a call
  // is active and only flip to "dead" on an explicit customer-hangup signal —
  // because live (WebRTC) calls never populate `sp.sessions` (they live in the
  // SIP context), so we can't infer "live" from a session there. The simulator
  // DOES populate `sp.sessions`, so a lingering `wrapping`-only session is also
  // treated as dead. `customerGone` resets on every new call (dialer:bridged /
  // screen-pop) so the badge can never stick across calls.
  const simHasSession = sp.sessions.length > 0;
  const simLiveLeg = sp.sessions.some(
    (s) => s.state === "dialing" || s.state === "ringing" ||
           s.state === "talking" || s.state === "held");
  const callDead = callActive && (customerGone || (simHasSession && !simLiveLeg));
  const callStatus = callActive ? (callDead ? "dead" : "live") : null;

  // NOTE: the DEAD-call → floor reporting (/me/call-dead) is owned by the
  // always-mounted Softphone (single source of truth across all dial modes),
  // so the Workspace no longer posts it here (avoids dual-writer races).

  // Live talk-timer — owned by Workspace (always mounted during a call) so it
  // never misses the `dialer:bridged` event, then passed down to the
  // (late-mounting) voice-only control bar. Mirrors how the always-mounted
  // ConversationView owns the timer in omnichannel mode.
  const { mmss: liveCallTimer, active: liveTimerActive } = useCallTimer(callStatus === "dead");

  // While a call is active, synthesise a minimal "live call" conversation so
  // the panes show contextual UI. Built whenever a call is bridged (independent
  // of any chat the agent may have open) so the AI Copilot can ALWAYS follow
  // the live voice call — never the omni-inbox chat — during a call.
  const liveCallConv = (callActive && contact)
    ? {
        id: `live-${contact.id}`,
        contact_id: contact.unknown ? null : contact.id,
        contact_name: contact.full_name || contact.phone || "Live call",
        primary_channel: "voice",
        live_call: true,
        call_status: callStatus,
        last_ts: new Date().toISOString(),
        messages: [
          {
            id: "live-start",
            from: "system",
            channel: "voice",
            ts: new Date().toISOString(),
            body: `Live call connected with ${contact.full_name || contact.phone}. Transcript will appear here once available.`,
          },
        ],
      }
    : null;

  function onPickCustomer(summary) {
    // Offline agents are not handling work — block opening any omni-channel
    // conversation until they go Available (or take a pause code). Voice
    // screen-pops (call-driven) bypass this since a live call is active.
    if (isOffline) {
      toast.error("You're Offline", {
        description: "Go Available to open and handle conversations.",
      });
      return;
    }
    setSelectedSummary(summary);
    setInboxView("active");
    setCallDriven(false);  // user-driven; don't auto-revert
  }

  function onSent(msg) {
    setConversation((c) => c ? { ...c, messages: [...(c.messages || []), msg], last_ts: msg.ts } : c);
  }

  function handleViewChange(v) {
    setInboxView(v);
    if (v === "list") setCallDriven(false);  // user explicitly returned to list
  }

  // Layout proportions: 37/37/26 with copilot, 50/50 without
  const cols = aiCopilot ? "37fr 37fr 26fr" : "1fr 1fr";

  // ─── Voice-only mode (tenant disabled the Omnichannel section) ───────────
  // The inbox + conversation panels are hidden; the workspace becomes a
  // full-width CRM lead view. The screen-pop pipeline still drives `contact`
  // for ALL call types exactly as before.
  if (!omniEnabled) {
    const activeSession = sp.sessions.find((s) => s.peer === contact?.phone) || sp.sessions[0] || null;
    const crmCard = (
      <div className="rounded-xl border border-border bg-card h-full overflow-hidden flex flex-col min-w-0">
          {callActive && (
            <div className="px-4 py-3 border-b border-border bg-muted/30 shrink-0" data-testid="voice-call-controls">
              <LiveCallControlBar status={callStatus} aiMonitoring={aiCopilot} timer={liveTimerActive ? liveCallTimer : undefined} />
            </div>
          )}
          {contact ? (
            contact.unknown ? (
              <div className="flex-1 grid place-items-center p-6" data-testid="voice-unknown-caller">
                <div className="text-center max-w-sm rounded-xl border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-950/20 p-6">
                  <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/60 mx-auto grid place-items-center">
                    <UserPlus className="w-7 h-7 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="mt-3 font-display text-lg font-bold">{contact.full_name || "Unknown caller"}</div>
                  <div className="text-sm font-mono text-muted-foreground">{contact.phone}</div>
                  <div className="mt-3 text-xs text-muted-foreground">No CRM record matched this number.</div>
                  <Button
                    data-testid="voice-save-unknown-btn"
                    variant="outline" size="sm" className="mt-3 gap-1.5"
                    onClick={async () => {
                      try {
                        const r = await api.post("/contacts", {
                          full_name: contact.full_name || `Caller ${contact.phone}`,
                          phone: contact.phone, email: "", company: "",
                        });
                        window.dispatchEvent(new CustomEvent("contact:created", { detail: r.data }));
                        setContact(r.data);
                      } catch { toast.error("Could not save contact"); }
                    }}
                  >
                    <UserPlus className="w-3.5 h-3.5" /> Save as contact
                  </Button>
                </div>
              </div>
            ) : (
              <ScrollArea className="flex-1 min-h-0">
                <CustomerProfilePanel contactId={contact.id} variant="drawer" activeSession={activeSession} onCall={callActive} />
              </ScrollArea>
            )
          ) : (
            <div className="flex-1 grid place-items-center" data-testid="voice-idle-state">
              <div className="text-center max-w-sm px-6">
                <div className="mx-auto w-20 h-20 rounded-2xl bg-muted grid place-items-center mb-4">
                  <Headset className="w-9 h-9 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl font-bold">Voice workspace</h3>
                <p className="text-sm text-muted-foreground mt-1">Customer CRM details will appear here when a call connects.</p>
              </div>
            </div>
          )}
        </div>
    );
    return (
      <div className="h-[calc(100vh-104px)] flex flex-col" data-testid="workspace-voice-only">
        <div className="px-3 lg:px-4 pt-3 lg:pt-4 shrink-0"><PreviewDialPanel /></div>
        <div
          className="grid gap-3 lg:gap-4 p-3 lg:p-4 pt-0 flex-1 min-h-0 grid-cols-1 lg:[grid-template-columns:var(--cols)]"
          style={{ "--cols": aiCopilot ? "1fr 360px" : "1fr" }}
        >
          {crmCard}
          {aiCopilot && (
            <section className="rounded-xl border border-border bg-card overflow-hidden flex-col min-w-0 hidden lg:flex" data-testid="voice-copilot-panel">
              <AiCopilot conversation={(callActive && contact) ? liveCallConv : null} />
            </section>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-104px)] flex flex-col">
      <div className="px-3 lg:px-4 pt-3 lg:pt-4 shrink-0"><PreviewDialPanel /></div>
      <div
        className="grid gap-3 lg:gap-4 p-3 lg:p-4 pt-0 flex-1 min-h-0 grid-cols-1 lg:[grid-template-columns:var(--cols)]"
        style={{ "--cols": cols }}
      >
        <section className="rounded-xl border border-border bg-card overflow-hidden flex flex-col min-w-0">
          <OmniInbox
            selectedId={selectedSummary?.id}
            onSelect={onPickCustomer}
            view={inboxView}
            onChangeView={handleViewChange}
            contact={contact}
            callDriven={callDriven}
            onCall={callActive}
            activeSession={sp.sessions.find((s) => s.peer === contact?.phone) || sp.sessions[0] || null}
            isOffline={isOffline}
          />
        </section>

        <section className="rounded-xl border border-border bg-card overflow-hidden flex flex-col min-w-0">
          <ConversationView conversation={conversation || liveCallConv} onSent={onSent} onCall={callActive} aiCopilot={aiCopilot} otherChannels={(callActive && contact) ? [] : (selectedSummary?.other_channels || [])} />
        </section>

        {aiCopilot && (
          <section className="rounded-xl border border-border bg-card overflow-hidden flex-col min-w-0 hidden lg:flex">
            <AiCopilot conversation={(callActive && contact) ? liveCallConv : conversation} />
          </section>
        )}
      </div>
    </div>
  );
}
